import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useAdminStatus } from '@/hooks/useAdminStatus';
import { Skeleton } from '@/components/ui/skeleton';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiresAdmin?: boolean;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  children, 
  requiresAdmin = false
}) => {
  const { user, isLoading } = useAuth();
  const { isAdmin, isLoading: adminLoading } = useAdminStatus();
  const location = useLocation();

  // Show loading skeleton while checking auth status
  if (isLoading || (requiresAdmin && adminLoading)) {
    return (
      <div className="min-h-screen bg-background p-4">
        <div className="container mx-auto max-w-4xl space-y-6">
          <Skeleton className="h-12 w-64" />
          <div className="grid gap-6">
            <Skeleton className="h-48 w-full" />
            <Skeleton className="h-32 w-full" />
            <Skeleton className="h-24 w-full" />
          </div>
        </div>
      </div>
    );
  }

  // Redirect to auth if not logged in
  if (!user) {
    return <Navigate to="/auth/" state={{ from: location }} replace />;
  }

  // If admin access is required but user is not admin
  if (requiresAdmin && !isAdmin) {
    return <Navigate to="/dashboard/" state={{ from: location }} replace />;
  }

  return <>{children}</>;
};

export default ProtectedRoute;